import { GoogleGenerativeAI } from '@google/generative-ai';
import { OnboardingProfile } from '@/types/onboarding';
import { DietType, DayPlan } from '@/store/nutritionStore';

// In a real app, use a backend proxy. For this demo, we use the client-side key.
// Ensure VITE_GEMINI_API_KEY is in your .env
const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY || '');

export const generateNutritionPlan = async (
    profile: OnboardingProfile,
    dietType: DietType,
    restrictions: string[]
): Promise<DayPlan[]> => {
    const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });

    const prompt = `
        Act as a world-class sports nutritionist for an elite wrestler.
        Create a 3-day sample meal plan (Day 1, Day 2, Day 3) optimized for performance.

        **Athlete Profile:**
        - Level: ${profile.level}
        - Wrestling Style: ${profile.matIdentity || 'Standard'}
        - Goal: ${profile.goals.join(', ')}
        - Weight Class Target: ${profile.weightClass || 'Mainteneance'}
        
        **Dietary Constraints:**
        - Diet Type: ${dietType}
        - Restrictions: ${restrictions.length > 0 ? restrictions.join(', ') : 'None'}

        **Format Requirements:**
        Return ONLY valid JSON. No markdown formatting. The structure must be an array of DayPlan objects:
        [
            {
                "day": "Day 1",
                "meals": {
                    "breakfast": { "name": "...", "calories": 500, "macros": { "protein": 40, "carbs": 50, "fats": 15 }, "description": "..." },
                    "lunch": { ... },
                    "dinner": { ... },
                    "snack": { ... }
                },
                "totalCalories": 2400,
                "totalMacros": { "protein": 180, "carbs": 250, "fats": 80 }
            }
        ]
    `;

    try {
        const result = await model.generateContent(prompt);
        const response = await result.response;
        const text = response.text();

        // Clean markdown code blocks if present
        const jsonString = text.replace(/```json/g, '').replace(/```/g, '').trim();

        return JSON.parse(jsonString);
    } catch (error) {
        console.error('Failed to generate plan:', error);
        throw new Error('AI Generation Failed');
    }
};
